import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import type { IInsertScheduledEmailRequest, IScheduledEmail, IScheduledEmailContentV2 } from '@/types/email.types';
import { ScheduledEmailsState } from './initial';
import {
  CreateScheduledEmail,
  GetEventScheduledEmails,
  GetChannelScheduledEmails,
  EditScheduledEmail,
  DeleteScheduledEmail,
} from '@/connection/scheduled-emails/scheduled-emails';
import { defaultCustomizations } from './defaults';

export const reducers = (create: ReducerCreators<ScheduledEmailsState>) => ({
  createScheduledEmail: create.asyncThunk(
    async (request: IInsertScheduledEmailRequest, { rejectWithValue }) => {
      try {
        return (await CreateScheduledEmail(request)) as IScheduledEmail;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.creatingEmail = true;
        state.errorCreatingEmail = false;
      },
      fulfilled: (state, action) => {
        state.emails = [action.payload, ...(state.emails ?? [])];
        state.creatingEmail = false;
        state.errorCreatingEmail = false;
      },
      rejected: (state) => {
        state.creatingEmail = false;
        state.errorCreatingEmail = true;
      },
    }
  ),

  loadEventScheduledEmails: create.asyncThunk(
    async (event_uuid: string, { rejectWithValue }) => {
      try {
        return (await GetEventScheduledEmails(event_uuid)) as IScheduledEmail[];
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.loadingEmails = true;
        state.errorLoadingEmails = false;
      },
      fulfilled: (state, action) => {
        state.emails = action.payload ?? [];
        state.loadingEmails = false;
        state.errorLoadingEmails = false;
      },
      rejected: (state) => {
        state.loadingEmails = false;
        state.errorLoadingEmails = true;
      },
    }
  ),

  loadChannelScheduledEmails: create.asyncThunk(
    async (channel: number, { rejectWithValue }) => {
      try {
        return (await GetChannelScheduledEmails(channel)) as IScheduledEmail[];
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.loadingEmails = true;
        state.errorLoadingEmails = false;
      },
      fulfilled: (state, action) => {
        state.emails = action.payload ?? [];
        state.loadingEmails = false;
        state.errorLoadingEmails = false;
      },
      rejected: (state) => {
        state.loadingEmails = false;
        state.errorLoadingEmails = true;
      },
    }
  ),

  setActiveEmail: create.reducer((state, action: PayloadAction<IScheduledEmail>) => {
    state.activeEmail = action.payload;
  }),

  clearActiveEmail: create.reducer((state) => {
    state.activeEmail = null;
  }),

  updateScheduledEmail: create.asyncThunk(
    async (email: IScheduledEmail, { rejectWithValue }) => {
      try {
        return (await EditScheduledEmail(email)) as IScheduledEmail;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.updatingEmail = true;
        state.errorUpdatingEmail = false;
      },
      fulfilled: (state, action) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = false;
        state.emails = (state.emails ?? []).map((e) => (e.uuid === action.payload.uuid ? action.payload : e));
        if (state.activeEmail?.uuid === action.payload.uuid) {
          state.activeEmail = action.payload;
        }
      },
      rejected: (state) => {
        state.updatingEmail = false;
        state.errorUpdatingEmail = true;
      },
    }
  ),

  patchScheduledEmailFields: create.reducer(
    (
      state,
      action: PayloadAction<{
        uuid: string;
        fields: Partial<Pick<IScheduledEmail, 'delivery_date' | 'timezone' | 'audiences' | 'email_type'>>;
      }>
    ) => {
      const { uuid, fields } = action.payload;
      const email = (state.emails ?? []).find((e) => e.uuid === uuid);
      if (!email) return;
      Object.assign(email, fields);
      if (state.activeEmail?.uuid === uuid) {
        Object.assign(state.activeEmail, fields);
      }
    }
  ),

  patchScheduledEmailCustomizations: create.reducer(
    (state, action: PayloadAction<{ uuid: string; fields: Partial<IScheduledEmailContentV2> }>) => {
      const { uuid, fields } = action.payload;
      const email = (state.emails ?? []).find((e) => e.uuid === uuid);
      if (!email) return;
      const existingV2: IScheduledEmailContentV2 =
        (email.customizations.v2 as IScheduledEmailContentV2 | undefined) ?? defaultCustomizations;

      const patched: IScheduledEmailContentV2 = {
        ...existingV2,
        ...(fields.title !== undefined ? { title: fields.title } : {}),
        ...(fields.salutation !== undefined ? { salutation: fields.salutation } : {}),
        ...(fields.description !== undefined ? { description: fields.description } : {}),
        ...(fields.startIntro !== undefined ? { startIntro: fields.startIntro } : {}),
        ...(fields.cta !== undefined ? { cta: fields.cta } : {}),
        ...(fields.subject !== undefined ? { subject: fields.subject } : {}),
        ...(fields.mainImage !== undefined ? { mainImage: fields.mainImage } : {}),
        ...(fields.headingImage !== undefined ? { headingImage: fields.headingImage } : {}),
        ...(fields.secondaryImage !== undefined ? { secondaryImage: fields.secondaryImage } : {}),
        ...(fields.backgroundColor !== undefined ? { backgroundColor: fields.backgroundColor } : {}),
        ...(fields.containerColor !== undefined ? { containerColor: fields.containerColor } : {}),
        ...(fields.secondaryAccentColor !== undefined ? { secondaryAccentColor: fields.secondaryAccentColor } : {}),
        ...(fields.secondaryBackgroundColor !== undefined
          ? { secondaryBackgroundColor: fields.secondaryBackgroundColor }
          : {}),
        ...(fields.accentColor !== undefined ? { accentColor: fields.accentColor } : {}),
        ...(fields.headingTextColor !== undefined ? { headingTextColor: fields.headingTextColor } : {}),
        ...(fields.bodyTextColor !== undefined ? { bodyTextColor: fields.bodyTextColor } : {}),
        ...(fields.buttonTextColor !== undefined ? { buttonTextColor: fields.buttonTextColor } : {}),
        ...(fields.templateId !== undefined ? { templateId: fields.templateId } : {}),
        ...(fields.campaignTitle !== undefined ? { campaignTitle: fields.campaignTitle } : {}),
      };

      if (!email.working_customizations) email.working_customizations = {};
      (email.working_customizations.v2 as IScheduledEmailContentV2) = patched;

      (email.customizations.v2 as IScheduledEmailContentV2) = patched;

      if (state.activeEmail?.uuid === uuid) {
        if (!state.activeEmail.working_customizations) state.activeEmail.working_customizations = {};
        (email.working_customizations.v2 as IScheduledEmailContentV2) = patched;
      }
    }
  ),

  deleteScheduledEmail: create.asyncThunk(
    async (uuid: string, { rejectWithValue }) => {
      try {
        await DeleteScheduledEmail(uuid);
        return uuid;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      pending: (state, action) => {
        state.deletingEmailUuid = action.meta.arg;
        state.errorDeletingEmail = false;
      },
      fulfilled: (state, action) => {
        state.emails = (state.emails ?? []).filter((e) => e.uuid !== action.payload);
        if (state.activeEmail?.uuid === action.payload) {
          state.activeEmail = null;
        }
        state.deletingEmailUuid = null;
        state.errorDeletingEmail = false;
      },
      rejected: (state) => {
        state.deletingEmailUuid = null;
        state.errorDeletingEmail = true;
      },
    }
  ),
});
